import { useMemo, useState, useRef, MouseEvent } from 'react'
import { useTranslation } from "react-i18next"
import { Link, Element, scroller } from 'react-scroll'
import styled from 'styled-components'
import CourseCard from '../components/CourseCard'
import { CourseDataType, CourseDataQuantityType, ToggleFeaturesType, CourseCategoryType, PrivateCourseType, PrivateRoomType } from '../types'
import { cloneObject, isPrivateCourseByName, isPrivateRoomByName } from '../helpers'
import { useDraggable } from "react-use-draggable-scroll";

interface SelectCourseSpecialCourseProps {
  isAdmin: boolean
  isAdminMode: boolean
  selectedCourses: CourseDataQuantityType[]
  coursesData: CourseDataType[]
  courseCategories: CourseCategoryType[]
  onBack: () => void
  onClick13More: () => void
  onClickNext: (courseDataQuantity: CourseDataQuantityType[]) => void
  currencySymbol: string
  bookingFeatures: ToggleFeaturesType
  listPrivateCourse: PrivateCourseType[]
  listPrivateRoom: PrivateRoomType[]
}

const SelectCourseSpecialCourse = ({coursesData, currencySymbol, isAdmin, isAdminMode, selectedCourses, courseCategories, bookingFeatures, onBack, onClick13More, onClickNext, listPrivateCourse, listPrivateRoom }: SelectCourseSpecialCourseProps) => {
  const { t } = useTranslation()
  const refScrollbarMenu = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;
  const { events } = useDraggable(refScrollbarMenu)
  const scrollMenuTimer = useRef<NodeJS.Timeout | null>(null)

  const [isMenuScrollBusy, setIsMenuScrollBusy] = useState(false)
  const [selectedCategoryID, setSelectedCategoryID] = useState<number>(courseCategories[0].id)
  const [quantityCourses, setQuantityCourses] = useState<CourseDataQuantityType[]>(selectedCourses)
  const maximumCourseQuantity = 20

  const totalQuantity = useMemo(() => {
    let total = 0
    quantityCourses.forEach(course => {
      total += course.quantity
    })
    return total
  }, [quantityCourses])

  // Find the minimum groupIDS
  let groupIDS: number[] = []
  quantityCourses.forEach(cat => {
    if (cat.quantity === 0) {
      return false
    }

    if (groupIDS.length > 0) {
      if (cat.groups.length > 0 && cat.groups.length < groupIDS.length) {
        groupIDS = cat.groups
      }
    } else {
      if (cat.groups.length > 0) {
        groupIDS = cat.groups
      }
    }
  })

  const selectedPrivateCourse = quantityCourses.find(selectedCourse => isPrivateCourseByName(selectedCourse.name, listPrivateCourse) && selectedCourse.quantity > 0)
  const hasSelectPrivateCourse = selectedPrivateCourse? true: false
  const selectedPrivateRoom = quantityCourses.find(selectedCourse => isPrivateRoomByName(selectedCourse.name, listPrivateRoom) && selectedCourse.quantity > 0)
  const hasSelectPrivateRoom = selectedPrivateRoom? true: false

  return (
    <div id="step-select-course">
      <CateWrapper>
        <CourseCategories 
          ref={refScrollbarMenu} 
          onMouseDown={(e: MouseEvent<HTMLInputElement>) => {
            refScrollbarMenu.current.style.scrollSnapType = "none";
            refScrollbarMenu.current.style.scrollBehavior = "initial";
            events.onMouseDown(e)
          }}
          onMouseUp={() => {
            refScrollbarMenu.current.style.removeProperty("scroll-snap-type");
            refScrollbarMenu.current.style.removeProperty("scroll-behavior");
          }}
        >
          {courseCategories.map((cate,i) => (
            <Link 
              key={i} 
              id={`nav-menu-${i+1}`}
              to={`${cate.id}`} 
              spy={true}
              smooth={true}
              offset={-60}
              duration={(scrollDistanceInPx) => {
                setBusyScrollTimeout(scrollDistanceInPx)
                return scrollDistanceInPx
              }}
              onSetActive={() => {
                if (!isMenuScrollBusy) {
                  handleClickTargetMenu(i)
                  setSelectedCategoryID(cate.id)
                }
              }}
            >
              <CourseCate 
                className={cate.id === selectedCategoryID? '-active': ''}
                onClick={(e: MouseEvent<HTMLInputElement>) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setSelectedCategoryID(cate.id)
                  handleClickTargetMenu(i)
                  scrollToElement(`${cate.id}`)
                }}
              >
                {cate.name}
              </CourseCate>
            </Link>
          ))}
        </CourseCategories>
      </CateWrapper>

      <CoursesOverflow>
        {
          courseCategories.map((cate, i) => {
            return (
              <Element key={i} name={`${cate.id}`}>
                <ListCouresCate>
                  <CateName>{cate.name}</CateName>
                  <CourseCards>
                    {
                      coursesData.filter(course => cate.course_ids.includes(course.id)).map((course, i) => {
                        return (
                          <CourseCard
                            key={i}
                            course={course}
                            quantityCourses={quantityCourses}
                            currencySymbol={currencySymbol}
                            maximumCourseQuantity={maximumCourseQuantity}
                            bookingFeatures={bookingFeatures}
                            totalQuantity={totalQuantity}
                            onUpdateQuantityCourse={handleUpdateQuantityCourse}
                            disabled={!canSelectCourse(course)}
                          />
                        )
                      })
                    }
                  </CourseCards>
                </ListCouresCate>
              </Element>
            )
          })
        }
      </CoursesOverflow>

      <NoticeServiceCharge className="yz-text-sm">{t('notice_price_shown_excluds_vat')}**</NoticeServiceCharge>

      <Footer>
        {
          isAdminMode ? (
            <BtnGroupBooking className="yuzu-btn -outline -lg" onClick={onBack}><span>{t('back')}</span></BtnGroupBooking>
          ): (
            <div></div>   
          )
        }
        <div 
          className="yuzu-btn -lg" 
          onClick={() => {
            let totalQuantity = 0
            quantityCourses.forEach(_course => totalQuantity += _course.quantity)
            if (totalQuantity === 0) {
              alert(`${t('warning_must_choose_course')}!`)
              return false
            }
            onClickNext(quantityCourses.filter(_course => _course.quantity !== 0 && canSelectCourse(_course)))
          }}>
            <span>{t('next')}</span>
          </div>
      </Footer>
    </div>
  )

  function canSelectCourse(course: CourseDataType) {
    let canSelectCourse = true
    groupIDS.every(group => {
      canSelectCourse = course.groups.includes(group)
      return !canSelectCourse
    })

    if (hasSelectPrivateCourse && selectedPrivateCourse) {
      canSelectCourse = selectedPrivateCourse.id === course.id
    }

    if (hasSelectPrivateRoom && selectedPrivateRoom) {
      canSelectCourse = selectedPrivateRoom.id === course.id
    }
    return canSelectCourse
  }

  function setBusyScrollTimeout(duration: number) {
    setIsMenuScrollBusy(true)
    if (scrollMenuTimer.current) {
      clearTimeout(scrollMenuTimer.current)
    }
    scrollMenuTimer.current = setTimeout(() => setIsMenuScrollBusy(false), duration + 50)
  }

  function getTargetScrollX(targetIndex: number) {
    const DOMMenuTarget = document.body.querySelector<HTMLElement>(`#nav-menu-${targetIndex + 1}`)
    if (DOMMenuTarget && refScrollbarMenu.current) {
      return DOMMenuTarget.offsetLeft
    } else {
      return 0
    }
  }

  function handleClickTargetMenu(menuIndex: number) {
    const targetScrollX = getTargetScrollX(menuIndex)
    
    if (refScrollbarMenu.current) {
      refScrollbarMenu.current.scrollTo(targetScrollX, 0)
    }
  }

  function handleUpdateQuantityCourse(courseQuantity: CourseDataQuantityType) {
    const { id, quantity, number_of_party } = courseQuantity
    const _quantityCourses: CourseDataQuantityType[] = cloneObject(quantityCourses)
    const targetQuantityCourse = _quantityCourses.find(course => course.id === id)

    // const minimumQuantity = 1

    if (targetQuantityCourse) {
      targetQuantityCourse.quantity = quantity
      targetQuantityCourse.number_of_party = number_of_party
    } else {
      _quantityCourses.push(courseQuantity)
    }

    setQuantityCourses(_quantityCourses)
  }

  function scrollToElement(elementName: string) {
    scroller.scrollTo(elementName, {
      smooth: true,
      offset: -60,
      duration: (scrollDistanceInPx: number) => {
        setBusyScrollTimeout(scrollDistanceInPx)
        return scrollDistanceInPx
      },
    })
  }
}

export default SelectCourseSpecialCourse

const CateWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;
  margin-bottom: 14px;
  margin-top: -30px;
  padding-top: 10px;
  background-color: black;
  padding-right: 40px;
  height: 40px;

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 60px;
    background: rgb(0, 0, 0);
    background: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 50%);
    pointer-events: none;
    z-index: 1;
  }

  &::after {
    content: "\\e907";
    font-family: 'fonts-icon-yuzu' !important;
    font-size: 16px;
    color: white;
    position: absolute;
    right: 0;
    pointer-events: none;
    z-index: 1;
    margin-top: -1px;
    top: 10px;

    @media (max-width: 767px) {
      right: 16px;
    }
  }

  @media (max-width: 767px) {
    width: calc(100% + 32px);
    margin-left: -16px;
    margin-top: -25px;
  }
`

const CourseCate = styled.div`
  position: relative;
  margin-bottom: 12px;
  color: white;
  cursor: pointer;
  display: table;
  text-decoration: none !important;
  scroll-snap-align: start;
  margin-right: 24px;
  text-transform: uppercase;
  user-select: none;

  &:focus {
    background-color: inherit !important;
    outline: none !important;
  }

  &.-active {
    position: relative;
    color: var(--global--color-background-secondary);
    font-weight: bold;

    &::after {
      content: '';
      display: block;
      height: 1px;
      width: 100%;
      background-color: var(--global--color-background-secondary);
      position: absolute;
      bottom: 2px;
    }
  }
`

const CourseCategories = styled.div`
  transform: translate3d(0, 0, 0);
  position: absolute;
  display: inline-flex;
  white-space: nowrap;
  width: 100%;
  overflow: hidden;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;

  &::scrollbar {
    display: none;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  a:last-child {
    ${CourseCate} {
      margin-right: 60px;
    }
  }

  @media (max-width: 767px) {
    scroll-padding: 0 16px;

    a:first-child ${CourseCate} {
      margin-left: 16px;
    }
  } 
`

const CateName = styled.div`
  text-transform: uppercase;
  margin-bottom: 2px;
`

const CourseCards = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: -12px;
  margin-right: -12px;
  margin-bottom: -24px;

  @media (max-width: 767px) {
    margin-bottom: -8px;
  }
`

const CoursesOverflow = styled.div`
  // height: 50vh;
  // overflow: auto;
  display: flex;
  flex-direction: column;
  row-gap: 30px;
`

const ListCouresCate = styled.div`
`

const NoticeServiceCharge = styled.div` 
  margin-top: 15px;
  color: white;
  text-align: right;
`

const Footer = styled.div`
  margin-top: 64px;
  margin-bottom: 80px;
  display: flex;
  justify-content: space-between;
`

const BtnGroupBooking = styled.div`
  &.yuzu-btn {
    padding-left: 48px !important;
    padding-right: 48px !important;

    @media (max-width: 767px) {
      padding-left: 16px !important;
      padding-right: 16px !important;
      flex-direction: column;
      align-items: center;
      padding-bottom: 18px;
    }
  }

  span:first-child::after {
    content: '';
    margin-right: .25em;
  }

  @media (max-width: 767px) {
    span:nth-child(2) {
      font-size: 10px;
    }
  }
`